<template>
  <CreatePage v-if="showCreate" :new="false" open-in-editor/>
  <div v-else class="inner-tap-list" :aria-busy="refreshing || switching">
    <div v-if="loading" class="inner-tap-list__loading" role="status" :aria-label="$t('Загрузка страниц')">
      <v-progress-circular indeterminate size="24" :width="2" color="primary"/>
    </div>
    <template v-else>
      <div v-if="loadError" class="inner-tap-list__error" role="alert">
        <span>{{ $t('Не удалось загрузить страницы') }}</span>
        <v-btn variant="text" color="primary" size="small" @click="loadTaps(true)">{{ $t('Повторить') }}</v-btn>
      </div>
      <ul class="inner-tap-list__items">
        <li v-for="tap in tapStore.tapList" :key="tap.id || tap.alias" class="inner-tap-list__item">
          <button
              type="button"
              class="inner-tap-list__tap"
              :aria-current="isCurrentTap(tap) ? 'page' : undefined"
              :disabled="switching"
              @click="selectTap(tap)"
          >
            <v-avatar
                size="44"
                :image="tap.previewBase64 ? `data:image/png;base64,${tap.previewBase64}` : undefined"
                :icon="PlaceholderIcon"
                color="BW-200"
                class="inner-tap-list__avatar"
            />
            <span class="inner-tap-list__info">
              <span class="inner-tap-list__name">{{ host }}/{{ tap.alias }}</span>
              <span v-if="tap.requestsCount > 0 || tap.messagesCount > 0" class="inner-tap-list__counters">
                <span v-if="tap.requestsCount > 0" class="inner-tap-list__counter">
                  {{ $t('{count} заявка', Number(tap.requestsCount)) }}
                </span>
                <span v-if="tap.messagesCount > 0" class="inner-tap-list__counter">
                  {{ $t('{count} сообщение', Number(tap.messagesCount)) }}
                </span>
              </span>
            </span>
            <span v-if="isCurrentTap(tap)" class="inner-tap-list__selected" aria-hidden="true">
              <v-icon :icon="CheckIcon" size="12"/>
            </span>
          </button>
        </li>
      </ul>
      <div v-if="switchError" class="inner-tap-list__error" role="alert">
        {{ $t('Не удалось открыть страницу') }}
      </div>
    </template>
    <v-btn
        variant="outlined"
        color="accent"
        rounded="lg"
        class="inner-tap-list__create"
        :disabled="switching"
        @click="showCreate = true"
    >
      <template #prepend>
        <v-icon :icon="AddIcon" size="24" color="BW-600-gray-text"/>
      </template>
      {{ $t('Создать новый тап') }}
    </v-btn>
  </div>
</template>

<script setup lang="ts">
import {onMounted, ref} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {useTapStore} from '@/stores/tapStore'
import {useDialogStore} from '@/stores/dialogStore'
import CreatePage from '@/components/CreatePage.vue'
import AddIcon from '~/assets/icons/add.vue'
import CheckIcon from '~/assets/icons/check.vue'
import PlaceholderIcon from '~/assets/icons/placeholder.vue'

const tapStore = useTapStore()
const dialogStore = useDialogStore()
const route = useRoute()
const router = useRouter()
const host = useRuntimeConfig().public.tapLink
const showCreate = ref(false)
const loading = ref(!tapStore.isInitialized && !tapStore.tapList.length)
const refreshing = ref(false)
const loadError = ref(false)
const switching = ref(false)
const switchError = ref(false)

const isCurrentTap = (tap: { alias: string }) => tap.alias === route.params.alias

const loadTaps = async (force = false) => {
  if (refreshing.value) return
  if (!force && tapStore.isInitialized && !tapStore.isTapListDirty) {
    loading.value = false
    return
  }

  refreshing.value = true
  loading.value = !tapStore.tapList.length
  loadError.value = false
  try {
    loadError.value = !await tapStore.getTapList({ preserveCurrentTap: true })
  } finally {
    loading.value = false
    refreshing.value = false
  }
}

const selectTap = async (tap: { alias: string }) => {
  if (switching.value) return
  if (isCurrentTap(tap)) {
    tapStore.moveTapToTop(tap.alias)
    dialogStore.close()
    return
  }

  switching.value = true
  switchError.value = false
  try {
    const failure = await router.replace({ path: `/profile/${tap.alias}/edit`, query: route.query })
    if (failure) {
      switchError.value = true
      return
    }
    tapStore.moveTapToTop(tap.alias)
    // Replace the modal history entry so closing cannot return to the previous tap.
    dialogStore.close(true)
  } catch {
    switchError.value = true
  } finally {
    switching.value = false
  }
}

onMounted(() => loadTaps())
</script>

<style scoped lang="sass">
.inner-tap-list
  display: flex
  flex-direction: column
  min-height: 0
  padding: 24px 0 8px

  &__loading,
  &__error
    display: flex
    align-items: center
    justify-content: center
    gap: 8px
    padding: 16px 0

  &__error
    flex-direction: column
    color: #6C757D
    font-size: 14px
    text-align: center

  &__items
    list-style: none
    margin: 0
    padding: 0
    overflow-y: auto
    max-height: 60dvh
    border-top: 1px solid #F0F3F4

  &__item
    padding: 8px 0
    box-shadow: inset 0 -1px #F0F3F4

  &__tap
    display: flex
    align-items: center
    gap: 12px
    width: 100%
    min-height: 60px
    padding: 8px
    border: 0
    border-radius: 8px
    background: transparent
    color: #363636
    text-align: left
    cursor: pointer

    &:active
      background: #F0F1F5

    &:focus-visible
      outline: 2px solid #0D87EF
      outline-offset: -2px

    &:disabled
      cursor: wait

    @media (hover: hover)
      &:hover
        background: #F0F1F5

  &__avatar
    flex: 0 0 44px

  &__info
    display: flex
    flex-direction: column
    gap: 4px
    min-width: 0
    flex: 1

  &__name
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap
    font-size: 14px
    font-weight: 600
    line-height: 18px

  &__counters
    display: flex
    flex-wrap: wrap
    gap: 4px 8px
    color: #6C757D
    font-size: 12px
    line-height: 16px

  &__counter
    display: inline-flex
    align-items: center
    gap: 4px

    &::before
      content: ''
      width: 6px
      height: 6px
      flex: 0 0 6px
      border-radius: 50%
      background: #D11635

  &__selected
    display: flex
    align-items: center
    justify-content: center
    width: 16px
    height: 16px
    flex: 0 0 16px
    border-radius: 50%
    background: #0D87EF

  &__create
    flex-shrink: 0
    width: 100%
    margin-top: 24px
</style>
